import React, { useState, useEffect, useRef } from "react";
import { useDealerAuth } from "../../context/DealerAuthContext";
import "./DealerProfile.css";

const API_BASE = process.env.REACT_APP_API_URL ? (process.env.REACT_APP_API_URL + '/api/v1') : '/api/v1';

function getToken() {
  return localStorage.getItem("dealerToken") || localStorage.getItem("authToken");
}

export default function DealerProfile() {
  const { dealer, logout } = useDealerAuth();
  const fileRef = useRef();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    name: "",
    address: "",
    shop_name: "",
    gst_number: "",
    aadhaar_image: null,
    aadhaar_preview: null,
  });

  // ── Fetch dealer profile on mount ──
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/dealer/profile`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load profile");
      setProfile(data);
      setForm({
        name: data.name || "",
        address: data.address || "",
        shop_name: data.shop_name || "",
        gst_number: data.gst_number || "",
        aadhaar_image: null,
        aadhaar_preview: data.aadhaar_image_url || null,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    // Validate
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!form.shop_name.trim()) { setError("Shop name is required"); return; }
    if (!form.address.trim()) { setError("Address is required"); return; }
    if (
      form.gst_number &&
      !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(form.gst_number)
    ) {
      setError("Invalid GST number format"); return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("address", form.address);
      fd.append("shop_name", form.shop_name);
      if (form.gst_number) fd.append("gst_number", form.gst_number);
      if (form.aadhaar_image) fd.append("aadhaar_image", form.aadhaar_image);

      const res = await fetch(`${API_BASE}/dealer/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Update failed");

      // Update localStorage dealer info
      const stored = JSON.parse(localStorage.getItem("dealerUser") || "{}");
      const updated = { ...stored, name: form.name, address: form.address };
      localStorage.setItem("dealerUser", JSON.stringify(updated));

      setProfile(data);
      setSuccess("Profile updated successfully!");
      setEditMode(false);

      // Refresh
      await fetchProfile();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setError("");
    setSuccess("");
    // Reset form to current profile
    setForm({
      name: profile?.name || "",
      address: profile?.address || "",
      shop_name: profile?.shop_name || "",
      gst_number: profile?.gst_number || "",
      aadhaar_image: null,
      aadhaar_preview: profile?.aadhaar_image_url || null,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((f) => ({
      ...f,
      aadhaar_image: file,
      aadhaar_preview: URL.createObjectURL(file),
    }));
  };

  const verificationColor = {
    pending: "#854d0e",
    verified: "#166534",
    rejected: "#991b1b",
  };
  const verificationBg = {
    pending: "#fef9c3",
    verified: "#dcfce7",
    rejected: "#fee2e2",
  };

  if (loading) {
    return (
      <div className="dp-loading">
        <div className="dp-spinner-lg" />
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="dp-root">
      {/* ── Header ── */}
      <div className="dp-header">
        <div>
          <h1 className="dp-title">Dealer Profile</h1>
          <p className="dp-subtitle">Manage your account and business details</p>
        </div>
        {!editMode && (
          <button className="dp-edit-btn" onClick={() => { setEditMode(true); setSuccess(""); setError(""); }}>
            ✏️ Edit Profile
          </button>
        )}
      </div>

      {/* ── Alerts ── */}
      {error && <div className="dp-alert dp-alert--error">⚠️ {error}</div>}
      {success && <div className="dp-alert dp-alert--success">✅ {success}</div>}

      <div className="dp-grid">
        {/* ── LEFT: Profile Card ── */}
        <div className="dp-card dp-profile-card">
          <div className="dp-avatar-wrap">
            <div className="dp-avatar-circle">
              {(profile?.name || "D").charAt(0).toUpperCase()}
            </div>
            <div
              className="dp-verification-badge"
              style={{
                background: verificationBg[profile?.verification_status] || "#f3f4f6",
                color: verificationColor[profile?.verification_status] || "#374151",
              }}
            >
              {profile?.verification_status === "verified" ? "✓ Verified" :
               profile?.verification_status === "rejected" ? "✗ Rejected" : "⏳ Pending Verification"}
            </div>
          </div>

          <div className="dp-profile-meta">
            <h2 className="dp-profile-name">{profile?.name || "—"}</h2>
            <p className="dp-profile-shop">{profile?.shop_name || "—"}</p>
            <p className="dp-profile-phone">📱 +91 {profile?.phone}</p>
          </div>

          <div className="dp-info-list">
            <div className="dp-info-row">
              <span className="dp-info-label">Referral Code</span>
              <span className="dp-info-val dp-referral-code">{profile?.referral_code || "—"}</span>
            </div>
            <div className="dp-info-row">
              <span className="dp-info-label">Member Since</span>
              <span className="dp-info-val">
                {profile?.created_at
                  ? new Date(profile.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })
                  : "—"}
              </span>
            </div>
            <div className="dp-info-row">
              <span className="dp-info-label">Wallet Balance</span>
              <span className="dp-info-val dp-wallet">₹{Number(profile?.wallet_balance || 0).toLocaleString("en-IN")}</span>
            </div>
            <div className="dp-info-row">
              <span className="dp-info-label">Coins</span>
              <span className="dp-info-val">🪙 {profile?.coins || 0}</span>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Edit / View Form ── */}
        <div className="dp-card dp-details-card">
          <h3 className="dp-section-title">
            {editMode ? "Edit Business Details" : "Business Details"}
          </h3>

          <div className="dp-form-grid">
            {/* Name */}
            <div className="dp-field">
              <label className="dp-label">Full Name <span className="dp-req">*</span></label>
              {editMode ? (
                <input className="dp-input" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your full name" />
              ) : (
                <div className="dp-value">{profile?.name || "—"}</div>
              )}
            </div>

            {/* Shop Name */}
            <div className="dp-field">
              <label className="dp-label">Shop / Business Name <span className="dp-req">*</span></label>
              {editMode ? (
                <input className="dp-input" value={form.shop_name}
                  onChange={(e) => setForm((f) => ({ ...f, shop_name: e.target.value }))}
                  placeholder="e.g. Sharma Motors" />
              ) : (
                <div className="dp-value">{profile?.shop_name || "—"}</div>
              )}
            </div>

            {/* Phone (read-only always) */}
            <div className="dp-field">
              <label className="dp-label">Phone Number</label>
              <div className="dp-value dp-value--muted">
                +91 {profile?.phone}
                {profile?.phone_verified && (
                  <span className="dp-verified-tag">✓ Verified</span>
                )}
              </div>
            </div>

            {/* GST */}
            <div className="dp-field">
              <label className="dp-label">
                GST Number
                <span className="dp-optional-tag">Optional</span>
              </label>
              {editMode ? (
                <input className="dp-input" value={form.gst_number}
                  onChange={(e) => setForm((f) => ({ ...f, gst_number: e.target.value.toUpperCase() }))}
                  placeholder="22AAAAA0000A1Z5" />
              ) : (
                <div className="dp-value">{profile?.gst_number || <span className="dp-empty-val">Not provided</span>}</div>
              )}
            </div>

            {/* Address */}
            <div className="dp-field dp-field--full">
              <label className="dp-label">Business Address <span className="dp-req">*</span></label>
              {editMode ? (
                <textarea className="dp-input dp-textarea" rows={3} value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Full business address" />
              ) : (
                <div className="dp-value">{profile?.address || "—"}</div>
              )}
            </div>

            {/* Aadhaar */}
            <div className="dp-field dp-field--full">
              <label className="dp-label">
                Aadhaar Card
                {editMode && <span className="dp-optional-tag">Click to update</span>}
              </label>
              {editMode ? (
                <div
                  className={`dp-upload-zone ${form.aadhaar_preview ? "dp-upload-zone--done" : ""}`}
                  onClick={() => fileRef.current?.click()}
                >
                  {form.aadhaar_preview ? (
                    <div className="dp-upload-preview">
                      <img src={form.aadhaar_preview} alt="Aadhaar" className="dp-aadhaar-img" />
                      <span className="dp-upload-change">Click to change</span>
                    </div>
                  ) : (
                    <div className="dp-upload-placeholder">
                      <span>📄</span>
                      <span>Click to upload Aadhaar card</span>
                      <span className="dp-upload-hint">JPG, PNG or PDF · Max 5MB</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="dp-aadhaar-view">
                  {profile?.aadhaar_image_url ? (
                    <a href={profile.aadhaar_image_url} target="_blank" rel="noreferrer" className="dp-aadhaar-link">
                      <img src={profile.aadhaar_image_url} alt="Aadhaar" className="dp-aadhaar-thumb" />
                      <span>View Aadhaar</span>
                    </a>
                  ) : (
                    <span className="dp-empty-val">No Aadhaar uploaded</span>
                  )}
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*,.pdf"
                style={{ display: "none" }} onChange={handleFileChange} />
            </div>
          </div>

          {/* ── Action Buttons ── */}
          {editMode && (
            <div className="dp-actions">
              <button className="dp-cancel-btn" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
              <button className="dp-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? <span className="dp-spinner-sm" /> : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Account Stats ── */}
      <div className="dp-stats-row">
        <div className="dp-stat-card">
          <div className="dp-stat-icon" style={{ background: "#dbeafe" }}>🚗</div>
          <div className="dp-stat-info">
            <div className="dp-stat-label">Account Status</div>
            <div className="dp-stat-val" style={{ color: profile?.status === "active" ? "#166534" : "#991b1b" }}>
              {profile?.status === "active" ? "Active" : profile?.status || "—"}
            </div>
          </div>
        </div>
        <div className="dp-stat-card">
          <div className="dp-stat-icon" style={{ background: "#fef9c3" }}>📋</div>
          <div className="dp-stat-info">
            <div className="dp-stat-label">Verification Status</div>
            <div className="dp-stat-val" style={{ color: verificationColor[profile?.verification_status] }}>
              {profile?.verification_status
                ? profile.verification_status.charAt(0).toUpperCase() + profile.verification_status.slice(1)
                : "—"}
            </div>
          </div>
        </div>
        <div className="dp-stat-card">
          <div className="dp-stat-icon" style={{ background: "#dcfce7" }}>💰</div>
          <div className="dp-stat-info">
            <div className="dp-stat-label">Wallet Balance</div>
            <div className="dp-stat-val" style={{ color: "#059669" }}>
              ₹{Number(profile?.wallet_balance || 0).toLocaleString("en-IN")}
            </div>
          </div>
        </div>
        <div className="dp-stat-card">
          <div className="dp-stat-icon" style={{ background: "#ede9fe" }}>🪙</div>
          <div className="dp-stat-info">
            <div className="dp-stat-label">Coins Earned</div>
            <div className="dp-stat-val" style={{ color: "#7c3aed" }}>
              {profile?.coins || 0}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}